import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';

interface VersionLog {
  version: string;
  date: string;
  type: 'major' | 'minor' | 'patch';
  features: string[];
  fixes: string[];
  improvements: string[];
}

export default function SystemMaintenancePage() {
  const [versionLogs] = useState<VersionLog[]>([
    {
      version: 'v2.5.0',
      date: '2024-01-15',
      type: 'minor',
      features: [
        '新增云电脑快照功能',
        '支持批量导入用户',
        '新增工单系统自动分配规则',
        '增加数据统计导出功能'
      ],
      fixes: [
        '修复订单支付回调异常问题',
        '修复用户列表筛选条件不生效的问题',
        '修复云电脑到期提醒发送失败的问题'
      ],
      improvements: [
        '优化系统性能，提升页面加载速度',
        '改进用户界面交互体验',
        '优化数据库查询效率'
      ]
    },
    {
      version: 'v2.4.2',
      date: '2024-01-08',
      type: 'patch',
      features: [],
      fixes: [
        '修复邮件发送模板上传失败问题',
        '修复租户管理页面数据显示错误',
        '修复操作日志记录不完整的问题'
      ],
      improvements: ['优化短信发送成功率', '改进错误提示信息']
    },
    {
      version: 'v2.4.0',
      date: '2024-01-01',
      type: 'minor',
      features: [
        '新增消息通知管理功能',
        '支持自定义通知模板',
        '新增发送记录查询功能',
        '增加用户标签管理'
      ],
      fixes: [
        '修复云电脑释放后数据未清理的问题',
        '修复积分充值记录显示错误'
      ],
      improvements: [
        '优化通知发送机制',
        '改进数据统计准确性',
        '提升系统稳定性'
      ]
    },
    {
      version: 'v2.3.0',
      date: '2023-12-20',
      type: 'minor',
      features: [
        '新增定时任务管理功能',
        '支持自定义任务执行周期',
        '新增任务执行日志查看',
        '增加系统配置管理'
      ],
      fixes: ['修复用户实名认证信息显示问题', '修复订单列表分页错误'],
      improvements: ['优化系统配置界面', '改进任务调度机制']
    }
  ]);

  const getVersionTypeColor = (type: string) => {
    switch (type) {
      case 'major':
        return 'bg-red-100 text-red-700';
      case 'minor':
        return 'bg-blue-100 text-blue-700';
      case 'patch':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getVersionTypeText = (type: string) => {
    switch (type) {
      case 'major':
        return '重大版本';
      case 'minor':
        return '功能版本';
      case 'patch':
        return '修复版本';
      default:
        return '未知';
    }
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">系统维护</h1>
          <p className="text-sm text-gray-500 mt-1">系统版本管理和更新日志</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-information-line text-teal-600"></i>
            当前版本信息
          </h2>
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                  <i className="ri-code-box-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-xs text-teal-700 font-medium">当前版本</div>
                  <div className="text-2xl font-bold text-teal-900">v2.5.0</div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <i className="ri-calendar-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-xs text-blue-700 font-medium">发布日期</div>
                  <div className="text-lg font-bold text-blue-900">2024-01-15</div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-xs text-purple-700 font-medium">系统状态</div>
                  <div className="text-lg font-bold text-purple-900">运行正常</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-5 flex items-center gap-2">
            <i className="ri-history-line text-teal-600"></i>
            版本更新日志
          </h2>

          <div className="space-y-6">
            {versionLogs.map((log, index) => (
              <div
                key={log.version}
                className={`${
                  index !== versionLogs.length - 1 ? 'pb-6 border-b border-gray-200' : ''
                }`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-xl font-bold text-gray-900">{log.version}</h3>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${getVersionTypeColor(
                      log.type
                    )}`}
                  >
                    {getVersionTypeText(log.type)}
                  </span>
                  <span className="text-sm text-gray-500">{log.date}</span>
                </div>

                <div className="space-y-4">
                  {log.features.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-star-line text-teal-600"></i>
                        <h4 className="text-sm font-semibold text-gray-900">新增功能</h4>
                      </div>
                      <ul className="space-y-1 ml-6">
                        {log.features.map((feature, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-teal-600 mt-1">•</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {log.fixes.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-bug-line text-orange-600"></i>
                        <h4 className="text-sm font-semibold text-gray-900">问题修复</h4>
                      </div>
                      <ul className="space-y-1 ml-6">
                        {log.fixes.map((fix, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-orange-600 mt-1">•</span>
                            <span>{fix}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {log.improvements.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-arrow-up-circle-line text-blue-600"></i>
                        <h4 className="text-sm font-semibold text-gray-900">优化改进</h4>
                      </div>
                      <ul className="space-y-1 ml-6">
                        {log.improvements.map((improvement, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-blue-600 mt-1">•</span>
                            <span>{improvement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
